import {
  Combine,
  Scissors,
  RotateCw,
  ImagePlus,
  ListOrdered,
  FileImage,
  FileDown,
  Unlock,
  Square,
  FileText,
  LayoutGrid,
  PenLine,
  TextSelect,
  Wand2,
  FileType2,
} from 'lucide-react';
import type { Tool } from '@/registry/types';
import { PdfMerge } from './PdfMerge';
import { PdfSplit } from './PdfSplit';
import { PdfRotate } from './PdfRotate';
import { ImagesToPdf } from './ImagesToPdf';
import { PdfRearrange } from './PdfRearrange';
import { PdfToImages } from './PdfToImages';
import { PdfCompress } from './PdfCompress';
import { PdfUnlock } from './PdfUnlock';
import { PdfRedact } from './PdfRedact';
import { MarkdownToPdf } from './MarkdownToPdf';
import { PdfThumbnails } from './PdfThumbnails';
import { PdfSigner } from './PdfSigner';
import { PdfTextExtract } from './PdfTextExtract';
import { TemplatePdf } from './TemplatePdf';
import { PdfToWord } from './PdfToWord';

export const documentTools: Tool[] = [
  {
    id: 'pdf-merge',
    name: 'Merge PDFs',
    category: 'documents',
    description: 'Combine multiple PDFs into one. Reorder before merge.',
    keywords: ['pdf', 'merge', 'combine', 'join', 'concat'],
    icon: Combine,
    component: PdfMerge,
    featured: true,
    accepts: 'pdf',
    inBrowser: true,
  },
  {
    id: 'pdf-split',
    name: 'Split PDF',
    category: 'documents',
    description: 'Extract page ranges or split into single-page files.',
    keywords: ['pdf', 'split', 'extract', 'pages'],
    icon: Scissors,
    component: PdfSplit,
    featured: true,
    accepts: 'pdf',
    inBrowser: true,
  },
  {
    id: 'pdf-thumbnails',
    name: 'Visual page reorder',
    category: 'documents',
    description: 'Drag thumbnails to reorder, duplicate, or delete pages.',
    keywords: ['pdf', 'thumbnail', 'reorder', 'organize', 'visual'],
    icon: LayoutGrid,
    component: PdfThumbnails,
    accepts: 'pdf',
    inBrowser: true,
  },
  {
    id: 'pdf-signer',
    name: 'PDF signer',
    category: 'documents',
    description: 'Draw a signature, then click on the PDF to stamp it. Export signed copy.',
    keywords: ['pdf', 'sign', 'signature', 'esign', 'document'],
    icon: PenLine,
    component: PdfSigner,
    accepts: 'pdf',
    badge: 'new',
    inBrowser: true,
  },
  {
    id: 'pdf-text-extract',
    name: 'PDF text extract',
    category: 'documents',
    description: 'Pull all text out of a PDF, search across pages, copy or save.',
    keywords: ['pdf', 'text', 'extract', 'search', 'find', 'words'],
    icon: TextSelect,
    component: PdfTextExtract,
    featured: true,
    accepts: 'pdf',
    inBrowser: true,
  },
  {
    id: 'pdf-to-word',
    name: 'PDF → Word (.docx)',
    category: 'documents',
    description: 'Extract text and rebuild as a Word document with bold, italic, and headings.',
    keywords: ['pdf', 'word', 'docx', 'convert', 'export', 'editable'],
    icon: FileType2,
    component: PdfToWord,
    featured: true,
    accepts: 'pdf',
    badge: 'new',
    inBrowser: true,
  },
  {
    id: 'template-pdf',
    name: 'Template PDF generator',
    category: 'documents',
    description: 'Upload a PDF with {{placeholders}}, fill values, get a filled PDF.',
    keywords: ['template', 'pdf', 'fill', 'merge', 'mail merge', 'placeholder', 'variable'],
    icon: Wand2,
    component: TemplatePdf,
    featured: true,
    accepts: 'pdf',
    badge: 'new',
    inBrowser: true,
  },
  {
    id: 'pdf-rotate',
    name: 'Rotate PDF pages',
    category: 'documents',
    description: 'Rotate any subset of pages by 90/180/270.',
    keywords: ['pdf', 'rotate', 'orientation', 'flip'],
    icon: RotateCw,
    component: PdfRotate,
    accepts: 'pdf',
    inBrowser: true,
  },
  {
    id: 'pdf-rearrange',
    name: 'Reorder PDF pages',
    category: 'documents',
    description: 'Reorder, duplicate, or drop pages by number.',
    keywords: ['pdf', 'reorder', 'rearrange', 'duplicate', 'remove'],
    icon: ListOrdered,
    component: PdfRearrange,
    accepts: 'pdf',
    inBrowser: true,
  },
  {
    id: 'pdf-compress',
    name: 'Compress PDF',
    category: 'documents',
    description: 'Re-encode pages as JPEG to reduce file size.',
    keywords: ['pdf', 'compress', 'shrink', 'reduce', 'optimize', 'smaller'],
    icon: FileDown,
    component: PdfCompress,
    accepts: 'pdf',
    inBrowser: true,
  },
  {
    id: 'pdf-to-images',
    name: 'PDF → Images',
    category: 'documents',
    description: 'Render every page to PNG, JPEG, or WebP. Bulk ZIP export.',
    keywords: ['pdf', 'image', 'png', 'jpg', 'render', 'extract'],
    icon: FileImage,
    component: PdfToImages,
    accepts: 'pdf',
    inBrowser: true,
  },
  {
    id: 'pdf-unlock',
    name: 'Unlock PDF',
    category: 'documents',
    description: 'Remove password protection. Outputs an unrestricted PDF.',
    keywords: ['pdf', 'unlock', 'password', 'remove', 'decrypt'],
    icon: Unlock,
    component: PdfUnlock,
    accepts: 'pdf',
    inBrowser: true,
  },
  {
    id: 'pdf-redact',
    name: 'Redact PDF',
    category: 'documents',
    description: 'Drag to draw black rectangles over sensitive content.',
    keywords: ['pdf', 'redact', 'hide', 'censor', 'black out', 'privacy'],
    icon: Square,
    component: PdfRedact,
    accepts: 'pdf',
    inBrowser: true,
  },
  {
    id: 'images-to-pdf',
    name: 'Images → PDF',
    category: 'documents',
    description: 'Combine images into a single PDF with page sizes.',
    keywords: ['image', 'pdf', 'jpg', 'png', 'photo'],
    icon: ImagePlus,
    component: ImagesToPdf,
    accepts: 'image',
    inBrowser: true,
  },
  {
    id: 'md-to-pdf',
    name: 'Markdown → PDF',
    category: 'documents',
    description: 'Render markdown to a printable PDF with bold, italic, lists.',
    keywords: ['markdown', 'md', 'pdf', 'export', 'document'],
    icon: FileText,
    component: MarkdownToPdf,
    inBrowser: true,
  },
];
